import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { useWorkerStats } from "@src/appV2/Accounts/WorkplaceReferrals/api/useWorkerStats";
import { useGetWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { useFetchSubstage } from "../../ClipboardScore/hooks/useFetchSubstage";
import { useShouldEducateNewWorker } from "../Booking/useShouldEducateNewWorker";
import { useShouldShowClipboardScoreEducation } from "../Booking/useShouldShowClipboardScoreEducation";
import type { ShiftBundle } from "./types";
import { useRecentWorkplaceWorkedShiftCounts } from "./useRecentWorkplaceWorkedShiftCounts";
import { useShiftBundlesConfig } from "./useShiftBundlesConfig";
import { groupShiftsIntoBundles } from "./utils";

interface UseShiftBundlesOptions {
  eligibleWorkplaceIds: Set<string>;
}

export function useShiftBundles(
  shifts: OpenShift[],
  options: UseShiftBundlesOptions
): Map<string, ShiftBundle> {
  const config = useShiftBundlesConfig();
  const { eligibleWorkplaceIds } = options;
  const worker = useDefinedWorker();

  // Fetch worker stats to determine if the worker should see bundles.
  // Workers who would require new education or are limited by basket-size
  // are excluded
  const { data: workerStats } = useWorkerStats({ agentId: worker.userId });
  const workerPastShiftsCount = workerStats?.completedShiftsCount;

  const { shouldEducateNewWorker } = useShouldEducateNewWorker(workerPastShiftsCount);
  const { shouldShowClipboardScoreEducation } =
    useShouldShowClipboardScoreEducation(workerPastShiftsCount);

  const subStage = useFetchSubstage();
  const isWorkerBasketSizeLimited = subStage.data?.name === "LIMITED";

  const {
    recentWorkplaceWorkedShiftCounts,
    isLoading: isLoadingRecentWorkplaces,
    isError: isErrorRecentWorkplaces,
  } = useRecentWorkplaceWorkedShiftCounts({
    workerId: worker.userId,
    lookbackDays: config?.enabled ? config.recentlyWorkedThresholdInDays : 0,
    enabled: Boolean(config?.enabled),
  });

  // Fetch booked shifts
  const {
    shifts: bookedShifts,
    isLoading: isLoadingBookedShifts,
    isErrored: isErroredBookedShifts,
  } = useGetWorkerShifts({ worker });

  // Derive stable primitives from Set/Map so the downstream useMemo doesn't re-run on every render.
  // These recompute every render (their inputs are reference-unstable), but since the resulting
  // strings are compared by value, the expensive groupShiftsIntoBundles call is skipped when contents haven't changed.
  const eligibleWorkplaceIdsKey = [...eligibleWorkplaceIds].sort().join(",");
  const recentWorkplaceWorkedShiftCountsKey = [...recentWorkplaceWorkedShiftCounts.entries()]
    .sort(([workplaceIdA], [workplaceIdB]) => workplaceIdA.localeCompare(workplaceIdB))
    .map(([id, count]) => `${id}:${count}`)
    .join(",");

  return useMemo(() => {
    if (!config.enabled) {
      return new Map<string, ShiftBundle>();
    }

    if (shouldEducateNewWorker || shouldShowClipboardScoreEducation || isWorkerBasketSizeLimited) {
      return new Map<string, ShiftBundle>();
    }

    if (
      isLoadingBookedShifts ||
      isErroredBookedShifts ||
      isLoadingRecentWorkplaces ||
      isErrorRecentWorkplaces
    ) {
      return new Map<string, ShiftBundle>();
    }

    const formattedBookedShifts = bookedShifts.map((shift) => ({
      start: shift.start,
      end: shift.end,
      workplaceId: shift.facility.userId ?? "",
      qualification: shift.agentReq,
    }));

    return groupShiftsIntoBundles({
      shifts,
      config,
      eligibleWorkplaceIds,
      recentWorkplaceWorkedShiftCounts,
      bookedShifts: formattedBookedShifts,
    });
    // eligibleWorkplaceIds is intentionally replaced by eligibleWorkplaceIdsKey for stable memoisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shifts,
    config,
    shouldEducateNewWorker,
    shouldShowClipboardScoreEducation,
    isWorkerBasketSizeLimited,
    eligibleWorkplaceIdsKey,
    recentWorkplaceWorkedShiftCountsKey,
    isLoadingRecentWorkplaces,
    isErrorRecentWorkplaces,
    bookedShifts,
    isLoadingBookedShifts,
    isErroredBookedShifts,
  ]);
}
